/**
 * Form Submission Confirmation Component
 * =====================================
 *
 * Success confirmation page displayed after a form has been successfully submitted.
 * Adapts its messaging based on the submission result — specifically whether the
 * confirmation email was sent successfully or encountered a downstream issue.
 *
 * Purpose:
 * - Confirms successful form submission to the user
 * - Displays form-specific confirmation message
 * - Conditionally informs user about email confirmation or surfaces a warning
 * - Provides professional completion experience
 *
 * Data Flow:
 * - Receives form type from route parameter
 * - Receives optional warning from router navigation state
 * - Retrieves form metadata for display title
 * - Falls back to formatted form type if metadata unavailable
 * - Uses computed signals for reactive updates
 *
 * Route Integration:
 * - Activated by routing after successful form submission
 * - Route parameter: /form-submission-confirmation/:formType
 * - Optional router state: { warning: string }
 *
 * @author MacSafety Development Team
 * @version 1.1
 */

import { Component, computed, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormModelService } from '../../../../services/form-model.service';

@Component({
  selector: 'app-submission-confirmation',
  template: `
    <ion-card class="ion-text-center ion-justify-content-center no-card-style">

      <ion-card-header class="ion-text-center ion-align-items-center">
        <!-- Success icon changes to warning if email failed -->
        <ion-icon
          [name]="warning() ? 'warning' : 'checkmark-circle'"
          [color]="warning() ? 'warning' : 'success'"
          size="large">
        </ion-icon>

        <ion-card-title>
          Thank you for submitting your {{ formTypeDisplay() }}
        </ion-card-title>
      </ion-card-header>

      <ion-card-content>
        <!--
          EMAIL STATUS MESSAGE
          =====================
          Shows standard email confirmation message, or a warning if a downstream
          issue was detected (e.g. PDF generation or email service unavailable).
        -->
        <ion-text [color]="warning() ? 'warning' : 'medium'">
          <p *ngIf="!warning()">You will receive a copy of your report in your email inbox shortly.</p>
          <p *ngIf="warning()">{{ warning() }}</p>
          <br>
        </ion-text>

        <ion-text color="medium">
          <p><strong>Thanks!</strong></p>
          <p>MacMiller Safety Team</p>
        </ion-text>
      </ion-card-content>

    </ion-card>
  `
})
export class SubmissionConfirmationComponent {

  // ============================================================================
  // #region Signals
  // ============================================================================

  /**
   * Optional warning message passed via router navigation state.
   * Populated when the form saved successfully but a downstream service
   * (PDF generation or email) encountered an issue.
   */
  warning = signal<string | null>(null);

  // #endregion

  // ============================================================================
  // #region Lifecycle
  // ============================================================================

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formModelService: FormModelService
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state as { warning?: string } | undefined;
    if (state?.warning) {
      this.warning.set(state.warning);
    }
  }

  // #endregion

  // ============================================================================
  // #region Computed Properties
  // ============================================================================

  /**
   * Gets the form type from the route parameter.
   *
   * @returns The form type identifier from the route, or empty string if not found
   */
  formType = computed(() => {
    return this.route.snapshot.paramMap.get('formType') || '';
  });

  /**
   * Gets the display title for the form type.
   * Attempts to retrieve the human-readable title from form metadata.
   * Falls back to the raw form type if metadata is unavailable.
   *
   * @returns Human-readable form title or formatted form type identifier
   */
  formTypeDisplay = computed(() => {
    const metadata = this.formModelService.getFormMetadata(this.formType());
    if (metadata?.title) {
      return metadata.title;
    }
    return this.formType();
  });

  // #endregion
}